// Step 7b of the client-side X12 pipeline: the two browser-only primitives
// the page uses for upload and export. Neither function ever sends its
// content anywhere — reading a File happens entirely in-memory in this tab,
// and downloading is a local Blob URL clicked by a detached anchor.

/**
 * Reads a File's content as text. Only ever called with a file the user
 * picked from their own device — its bytes are read in-memory and never
 * transmitted.
 */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('Could not read the selected file.'));
    };
    reader.readAsText(file);
  });
}

/**
 * Triggers a browser download of `content` as a local file named `filename`.
 * This is the only way the exported document leaves the tab — as a file the
 * browser saves locally, never a network request.
 */
export function downloadTextFile(filename: string, content: string): void {
  const blob = new Blob([content], {type: 'text/plain'});
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  URL.revokeObjectURL(url);
}
